
import React from 'react';
import { format } from 'date-fns';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Signal } from '@/types/signal';
import { Skeleton } from '@/components/ui/skeleton';

interface PremiumBotSignalsTableProps {
  signals: Signal[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const PremiumBotSignalsTable = ({ 
  signals, 
  isLoading
}: PremiumBotSignalsTableProps) => {
  // Format date for better readability
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, HH:mm:ss');
    } catch (e) {
      return dateString;
    }
  };
  
  // Truncate signal ID to first 8 chars
  const truncateId = (id: string) => {
    return id.substring(0, 8);
  };
  
  // Get status badge styling
  const getStatusBadge = (status: string) => {
    switch(status.toLowerCase()) {
      case 'processed':
        return "bg-green-500/20 text-green-700 hover:bg-green-500/30 border-green-500/30";
      case 'failed':
        return "bg-red-500/20 text-red-700 hover:bg-red-500/30 border-red-500/30";
      case 'pending':
        return "bg-amber-500/20 text-amber-700 hover:bg-amber-500/30 border-amber-500/30";
      default:
        return "";
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="border rounded-md">
        <div className="w-full min-w-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Instrument</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (signals.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No signals found for this bot.
      </div>
    );
  }
  
  // Render signals table
  return (
    <div className="border rounded-md">
      <div className="w-full min-w-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Instrument</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {signals.map((signal) => (
              <TableRow key={signal.id}>
                <TableCell className="font-mono text-xs">
                  {formatDateTime(signal.signal_time)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {truncateId(signal.id)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {signal.action}
                  </Badge>
                </TableCell>
                <TableCell>{signal.instrument}</TableCell>
                <TableCell>{signal.amount}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={getStatusBadge(signal.status)}>
                    {signal.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
